import { useState, useEffect, useCallback } from 'react';
import { getManageProperty, vacateTenant, getRentLedger, addRentLedger } from '../lib/apiService';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MAIN_API_BASE } from '../lib/apiConfig';
import { History, Receipt, UserX, AlertCircle } from 'lucide-react';

const RentManagement = ({ propertyId, isPG, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState([]);

  const [paymentMode, setPaymentMode] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    month: new Date().toISOString().slice(0, 7), // YYYY-MM
    rent: '',
    electricity_bill: '',
    water_bill: '',
    municipality_fees: '',
    penalties: '',
    others: '',
    others_note: '',
    status: 'paid'
  });
  const [paying, setPaying] = useState(false);
  const [vacating, setVacating] = useState(false);

  const fetchData = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await getManageProperty(propertyId);
      if (res?.status) {
        setData(res.data);
        if (res.data.tenants?.id) {
          fetchLedger(res.data.tenants.id);
        }
      }
    } catch (err) {
      toast.error('Failed to load rent details');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  const fetchLedger = async (tenantId) => {
    try {
      const res = await getRentLedger(tenantId, 'property');
      if (res?.status) {
        setLedger(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleVacate = async () => {
    if (!window.confirm('Are you sure you want to vacate this tenant?')) return;
    setVacating(true);
    try {
      const res = await vacateTenant({
        tenant_id: data.tenants.id,
        property_id: propertyId
      });
      if (res?.status === false) {
        throw new Error(res.message || 'Failed to vacate');
      }
      toast.success('Tenant vacated');
      await fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.message || 'Failed to vacate');
    } finally {
      setVacating(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!data?.tenants?.id) return;
    setPaying(true);
    try {
      await addRentLedger({
        property_id: propertyId,
        tenant_id: data.tenants.id,
        tenant_type: 'property',
        ...paymentForm
      });
      toast.success('Payment logged');
      setPaymentMode(false);
      fetchLedger(data.tenants.id);
    } catch (err) {
      toast.error(err.message || 'Failed to log payment');
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <div className="p-4 text-center text-slate-500">Loading rent details...</div>;
  if (isPG) return <div className="p-4 bg-orange-50 text-orange-800 rounded-xl">PG/Hostel rent management is currently handled per bed.</div>;

  const currentTenant = data?.tenants;
  const history = data?.tenant_history || [];

  return (
    <div className="space-y-4">
      {/* Current Tenant */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide">Current Tenant</h4>
        {currentTenant ? (
          <div>
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-semibold text-slate-800">{currentTenant.tenant_name}</p>
                <p className="text-sm text-slate-600">{currentTenant.tenant_phone}</p>
                <p className="text-xs text-slate-500 mt-1">Moved in: {currentTenant.move_in_date || 'N/A'}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleVacate} disabled={vacating} className="text-red-600 hover:bg-red-50 hover:text-red-700">
                <UserX className="w-4 h-4 mr-1.5" /> Vacate
              </Button>
            </div>
            {currentTenant.document_url && (
              <a href={currentTenant.document_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">View Document</a>
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> No active tenant
          </div>
        )}
      </div>

      {/* Rent Ledger & Payments */}
      {currentTenant && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Rent Ledger</h4>
            <Button size="sm" onClick={() => setPaymentMode(!paymentMode)} variant={paymentMode ? "outline" : "default"} className={!paymentMode ? "bg-slate-900 text-white" : ""}>
              {paymentMode ? 'Cancel' : 'Log Payment'}
            </Button>
          </div>
          
          {paymentMode && (
            <form onSubmit={handleAddPayment} className="mb-4 bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Month (YYYY-MM)</Label>
                  <Input required type="month" value={paymentForm.month} onChange={e => setPaymentForm({...paymentForm, month: e.target.value})} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Rent Amount</Label>
                  <Input required type="number" value={paymentForm.rent} onChange={e => setPaymentForm({...paymentForm, rent: e.target.value})} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Electricity Bill</Label>
                  <Input type="number" value={paymentForm.electricity_bill} onChange={e => setPaymentForm({...paymentForm, electricity_bill: e.target.value})} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Water Bill</Label>
                  <Input type="number" value={paymentForm.water_bill} onChange={e => setPaymentForm({...paymentForm, water_bill: e.target.value})} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <select className="flex h-8 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950" value={paymentForm.status} onChange={e => setPaymentForm({...paymentForm, status: e.target.value})}>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              <Button type="submit" size="sm" disabled={paying} className="w-full bg-[#ee7923] hover:bg-[#d56b1f] text-white">
                {paying ? 'Saving...' : 'Save Payment'}
              </Button>
            </form>
          )}

          {ledger.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {ledger.map(entry => (
                <div key={entry.id} className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{entry.month}</p>
                    <p className="text-xs text-slate-500">Total: ₹{entry.total}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${entry.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {entry.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500 text-center py-4">No payment history found.</p>
          )}
        </div>
      )}

      {/* Tenant History */}
      {history.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide">Past Tenants</h4>
          <div className="space-y-3">
            {history.map(t => (
              <div key={t.id} className="pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                <p className="font-medium text-sm text-slate-800">{t.tenant_name}</p>
                <p className="text-xs text-slate-500">{t.tenant_phone}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Stayed: {t.move_in_date || 'N/A'} to {t.move_out_date || 'N/A'}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RentManagement;
